// terminal/src/core/session-controller.ts

/**
 * SessionController owns a single TerminalCore instance and its container div.
 * One SessionController per tab/session. Handles I/O wiring, show/hide, and lifecycle.
 */

import { TerminalCore } from './terminal';
import type { ITheme } from '@xterm/xterm';

export interface SessionControllerConfig {
  sessionId: string;
  parentContainer: HTMLElement;
  /** Optional per-tab theme override */
  theme?: ITheme;
  /** Fallback theme if no per-tab override */
  defaultTheme?: ITheme;
  /** Called when the terminal produces input (keystrokes) */
  onInput: (sessionId: string, data: string) => void;
  /** Called when the terminal resizes */
  onResize: (sessionId: string, rows: number, cols: number) => void;
}

export class SessionController {
  sessionId: string;
  private terminal: TerminalCore;
  private container: HTMLDivElement;
  private visible: boolean = false;

  constructor(config: SessionControllerConfig) {
    this.sessionId = config.sessionId;

    // Create a dedicated container div for this session's terminal
    this.container = document.createElement('div');
    this.container.className = 'whss-session-container';
    this.container.dataset.sessionId = config.sessionId;
    this.container.style.display = 'none';
    this.container.style.width = '100%';
    this.container.style.height = '100%';
    config.parentContainer.appendChild(this.container);

    // Create terminal with optional per-tab theme
    const theme = config.theme ?? config.defaultTheme;
    this.terminal = new TerminalCore({
      ...(theme && { theme }),
    });

    // Mount terminal into our container
    this.terminal.mount(this.container);

    // Wire input: keystrokes → onInput callback with our session_id
    this.terminal.onData((data: string) => {
      config.onInput(this.sessionId, data);
    });

    // Wire resize: terminal resize → onResize callback with our session_id
    this.terminal.onResize((size: { rows: number; cols: number }) => {
      config.onResize(this.sessionId, size.rows, size.cols);
    });
  }

  // --- Lifecycle ---

  show(): void {
    if (this.visible) return;
    this.container.style.display = 'block';
    this.visible = true;
    // Fit after becoming visible so dimensions are correct
    this.terminal.fit();
    this.terminal.focus();
  }

  hide(): void {
    if (!this.visible) return;
    this.container.style.display = 'none';
    this.visible = false;
  }

  dispose(): void {
    this.terminal.dispose();
    this.container.remove();
  }

  // --- I/O ---

  write(data: string | Uint8Array): void {
    this.terminal.write(data);
  }

  writeScrollback(lines: string[], _totalLines: number, _savedAt?: number): void {
    if (lines.length === 0) return;
    const separator = '\x1b[2m' + '─'.repeat(40) + ' scrollback restored ' + '─'.repeat(40) + '\x1b[0m\r\n';
    const scrollbackText = lines.join('\r\n') + '\r\n' + separator;
    this.terminal.write(scrollbackText);
  }

  // --- State ---

  fit(): { rows: number; cols: number } {
    return this.terminal.fit();
  }

  focus(): void {
    this.terminal.focus();
  }

  isVisible(): boolean {
    return this.visible;
  }

  getTerminal(): TerminalCore {
    return this.terminal;
  }

  // --- Session transfer ---

  updateSessionId(newSessionId: string): void {
    this.sessionId = newSessionId;
    this.container.dataset.sessionId = newSessionId;
    // onData callback captures `this.sessionId` by reference, so it auto-updates
  }
}
